import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, Wallet, Loader2, TrendingUp, TrendingDown } from "lucide-react";

const ROLE_DEFAULT_CATEGORY = {
  branch_manager: "gerente_sucursal",
  hr_admin: "rh_nomina",
  treasury: "tesoreria",
  legal: "legal",
};

function BonusRangeBar({ totalScore }) {
  const pct = Math.min(Math.max(totalScore, 0), 130);
  // Fix WH57 (2026-09-23): verde desde 96, igual que el bono (100% desde 96).
  return (
    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden relative">
      <div
        className={`h-full transition-all ${totalScore >= 100 ? "bg-emerald-500" : totalScore >= 96 ? "bg-green-400" : totalScore >= 90 ? "bg-yellow-400" : "bg-red-400"}`}
        style={{ width: `${(pct / 130) * 100}%` }}
      />
    </div>
  );
}

export default function OpsMiBono() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [categoryCode, setCategoryCode] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [scoring, setScoring] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ops/kpis/categories").then((r) => {
      setCategories(r.data || []);
      const def = ROLE_DEFAULT_CATEGORY[user?.role];
      if (def && r.data?.some((c) => c.code === def)) setCategoryCode(def);
      else if (r.data?.length) setCategoryCode(r.data[0].code);
    }).catch(() => setCategories([]));
  }, [user]);

  const load = useCallback(async () => {
    if (!categoryCode) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (user?.branch_id) params.set("branch_id", user.branch_id);
      const res = await api.get(`/ops/kpis/scoring/category/${categoryCode}?${params}`);
      setScoring(res.data);
    } catch {
      setScoring(null);
    } finally {
      setLoading(false);
    }
  }, [categoryCode, period, user]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      <button onClick={() => navigate("/ops/kpis")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="mi-bono-back">
        <ArrowLeft className="w-4 h-4" /> Volver a Mis KPIs
      </button>

      <div className="flex items-center gap-2">
        <Wallet className="w-6 h-6 text-[#1A1A1A]" />
        <h1 className="text-2xl font-bold text-slate-900">Mi bono</h1>
      </div>

      <div className="flex gap-2">
        {categories.length > 1 && (
          <select value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="mi-bono-category-select">
            {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        )}
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="mi-bono-period-input" />
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 h-64 animate-pulse" />
      ) : !scoring ? (
        <div className="text-center py-16 text-slate-400" data-testid="mi-bono-empty">No hay datos para este período.</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="mi-bono-summary">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-400 uppercase font-semibold">Puesto</div>
                <div className="text-lg font-bold text-slate-900">{scoring.category_name}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400 uppercase font-semibold">Score consolidado</div>
                <div className="text-3xl font-black text-slate-900" data-testid="mi-bono-score">{scoring.total_score}%</div>
              </div>
            </div>
            <BonusRangeBar totalScore={scoring.total_score} />
            {scoring.bonus && (
              <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${scoring.bonus.multiplier > 0 ? "bg-emerald-50" : "bg-red-50"}`} data-testid="mi-bono-range">
                <div className="flex items-center gap-2">
                  {scoring.bonus.multiplier > 0 ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-red-500" />}
                  <span className="font-semibold text-slate-800">{scoring.bonus.label}</span>
                </div>
                {scoring.bonus_config && (
                  <span className="text-sm text-slate-600">
                    {scoring.bonus_config.amount_type === "fixed"
                      ? `Bono base: $${scoring.bonus_config.amount_mxn} MXN`
                      : scoring.bonus_config.percentage ? `Bono base: ${scoring.bonus_config.percentage}% del salario` : "Bono según salario"}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">KPI</th>
                  <th className="px-4 py-2.5 text-right">Ponderación</th>
                  <th className="px-4 py-2.5 text-right">Resultado</th>
                  <th className="px-4 py-2.5 text-right">Puntos</th>
                </tr>
              </thead>
              <tbody>
                {scoring.kpis.map((k) => (
                  <tr key={k.kpi_id} className="border-t border-slate-50" data-testid={`mi-bono-kpi-row-${k.kpi_id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{k.name} {!k.captured && <span className="text-[10px] text-slate-400 ml-1">(sin capturar)</span>}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{k.weight_pct}%</td>
                    <td className="px-4 py-3 text-right text-slate-600">{k.result_pct}%</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{k.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
